import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useI18N } from "@sundae/react-hooks";
import {
  Balance,
  ConditionalWrapper,
  DateI18N,
  FadeInBasic,
  TableV2,
  TableV2Types,
  tableV2Utils,
  Text,
  toolkitCx,
  Tooltip,
} from "@sundae/ui-toolkit";
import { ITextProps } from "@sundae/ui-toolkit/dist/types/components/atoms/Text/Text";
import { AssetAmount } from "@sundaeswap/asset";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import cx from "classnames";
import isEqual from "lodash/isEqual";
import orderBy from "lodash/orderBy";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useCustomCompareMemo } from "use-custom-compare";
import { CARDANO_EXPLORER_LINK_MAP } from "../../../constants/cardano.constants";
import { NUMBER_TABLE_ITEMS } from "../../../constants/page.constants";
import { Transaction, TransactionType } from "../../../gql/generated/taste-test.sdk";
import { useTasteTestTransactionHistory } from "../../../gql/hooks/taste-test.query";
import { externalRoutes } from "../../../pages/routes";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { useProfileAppContext } from "../../../stores";
import { safariSafeDate } from "../../../utils/dates.utils";
import { getEnv } from "../../../utils/network.utils";
import { formatCurrencyToAda } from "../../../utils/number-format";
import CardanoExplorer from "../../CardanoExplorer";
import LpHoverCard from "../../LpHoverCard";

const TasteTestTransactionHistoryTable: FC = () => {
  const { t, currentLocale } = useI18N("taste-test");

  /* -------------------------------------------------------------------------------------------------
   * Table state and sorting configuration.
   * -----------------------------------------------------------------------------------------------*/
  const [numberOfRows, setNumberOfRows] = useState(NUMBER_TABLE_ITEMS / 2);
  const [sortConfig, setSortConfig] = useState<
    TableV2Types.ITableSortConfig<keyof Transaction | null>
  >({
    sortBy: null,
    direction: null,
  });

  /* -------------------------------------------------------------------------------------------------
   * All the necessary context hooks.
   * -----------------------------------------------------------------------------------------------*/
  const {
    state: { cardanoExplorer },
  } = useProfileAppContext();
  const {
    handlers: { setRefetchTransactionHistory },
    state: { refetchTransactionHistory, tasteTest },
  } = useTasteTestContext();

  /* -------------------------------------------------------------------------------------------------
   * Fetches the transaction history for the current taste test.
   * -----------------------------------------------------------------------------------------------*/
  const {
    data: transactionHistory,
    isLoading,
    refetch,
  } = useTasteTestTransactionHistory(
    tasteTest?.policyId || "",
    1000 * 60 * 60 // 1 hour
  );

  /**
   * Handles the "Load More" button click.
   */
  const handleShowMoreContributions = useCallback(
    () => setNumberOfRows((prev) => prev + NUMBER_TABLE_ITEMS / 2),
    []
  );

  /** Handles Sorting of the table data. */
  const handleSort = useCallback(
    (sortBy: keyof Transaction) => {
      tableV2Utils.handleTableV2Sorting<Transaction>(sortConfig, sortBy, setSortConfig);
    },
    [sortConfig]
  );

  /**
   * This data that will be used to render the proper table headers.
   */
  const tableHeaders: TableV2Types.THeaders[] = useMemo(() => {
    return [
      { sortingKey: "type", label: t("operation", { ns: "common" }) },
      { sortingKey: "depositDelta", label: t("amount", { ns: "common" }) },
      {
        sortingKey: "createdAt",
        label: t("date", { ns: "common" }),
      },
      {
        sortingKey: "txHash",
        label: t("transaction", { ns: "glossary" }),
      },
    ];
  }, [t]);

  const tableColumns = useMemo<TableV2Types.ITableColumn<Transaction>[]>(
    () => [
      {
        tableCellProps: {
          render: ({ type }) => {
            const variant: ITextProps["variant"] = type === "Withdraw" ? "error" : "success";
            return (
              <Text tag="span" variant={variant} size="xs">
                {t(type.toLowerCase(), { ns: "glossary" })}
              </Text>
            );
          },
        },
      },
      {
        tableCellProps: {
          render: ({ depositDelta, totalDelta, type }) => {
            const isClaim = type === TransactionType.Claim;
            const { amount, asset } = depositDelta[0];
            const lpAsset = totalDelta.find((delta) => !SundaeUtils.isAdaAsset(delta.asset));
            const assetAmount = new AssetAmount(Math.abs(Number(amount)), asset);
            const lpAssetAmount = new AssetAmount(
              Math.abs(Number(lpAsset?.amount ?? "0")),
              lpAsset?.asset
            );
            const value = isClaim ? lpAssetAmount : assetAmount;

            return (
              <div className="flex items-center gap-1">
                <ConditionalWrapper
                  condition={isClaim}
                  wrapper={(children) => (
                    <LpHoverCard lpAsset={lpAssetAmount}>{children}</LpHoverCard>
                  )}
                >
                  <Balance locale={currentLocale} showSymbol={!isClaim} size="xs" value={value} />
                  {isClaim ? (
                    <>
                      <Text tag="span" size="xs">
                        {t("lpToken", { ns: "glossary", context: "short" })}
                      </Text>
                      <InformationCircleIcon
                        className={cx("h-3 w-3", toolkitCx.layout.text.muted)}
                      />
                    </>
                  ) : (
                    <Tooltip
                      trigger={
                        <InformationCircleIcon
                          className={cx("h-3 w-3 cursor-help", toolkitCx.layout.text.muted)}
                        />
                      }
                      tooltipLabel={`${formatCurrencyToAda(
                        assetAmount.value.toNumber(),
                        currentLocale
                      )}`}
                    />
                  )}
                </ConditionalWrapper>
              </div>
            );
          },
        },
      },
      {
        tableCellProps: {
          render: ({ createdAt }) => {
            const safariSafe_createdAt = safariSafeDate(createdAt.format);
            const txDate = new Date(safariSafe_createdAt ?? "");
            const formattedDate = new Intl.DateTimeFormat(currentLocale, {
              dateStyle: "long",
              timeStyle: "long",
            }).format(txDate);

            return (
              <div className="flex items-center gap-1">
                <DateI18N
                  formatOpts={{
                    dateStyle: "short",
                  }}
                  locale={currentLocale}
                  value={txDate}
                  size="xs"
                  variant="muted"
                />
                <Tooltip
                  trigger={
                    <InformationCircleIcon className={cx("h-3 w-3", toolkitCx.layout.text.muted)} />
                  }
                  tooltipLabel={formattedDate}
                />
              </div>
            );
          },
        },
      },
      {
        tableCellProps: {
          render: ({ txHash }) => (
            <CardanoExplorer
              hash={txHash ?? ""}
              type={CARDANO_EXPLORER_LINK_MAP.transaction[cardanoExplorer]}
              size="xs"
              truncateFrom={5}
              truncateUntil={2}
              variant="info"
            />
          ),
        },
      },
    ],
    [cardanoExplorer, currentLocale, t]
  );

  const tableData = useCustomCompareMemo(
    () => {
      const result = transactionHistory
        // Filter out the transactions that are not deposits/updates or withdrawals
        ?.filter((tx) =>
          [
            TransactionType.Deposit,
            TransactionType.Withdraw,
            TransactionType.Update,
            TransactionType.Claim,
          ].includes(tx.type)
        )
        // Map the data to the table row props
        ?.map((tx) => {
          const env = getEnv();
          const explorerLink = `${externalRoutes.explorer[env][cardanoExplorer]}/${CARDANO_EXPLORER_LINK_MAP.transaction[cardanoExplorer]}/${tx.txHash}`;
          return {
            data: tx,
            tableRowProps: {
              onClick: () => window.open(explorerLink, "_blank", "noopener,noreferrer"),
            },
          };
        }) as TableV2Types.TTableRowData<Transaction>[];

      // Sort the data based on the sortConfig
      if (sortConfig.sortBy) {
        const { sortBy, direction } = sortConfig;
        return orderBy(
          result,
          [
            ({ data }) => {
              if (sortBy === "createdAt") {
                return data.createdAt.format;
              }
              if (sortBy === "type") {
                return data.type;
              }
              if (sortBy === "depositDelta") {
                return Math.abs(Number(data.depositDelta[0].amount));
              }
              if (sortBy === "txHash") {
                return data.txHash;
              }

              return data[sortBy as keyof Transaction];
            },
          ],
          [direction as boolean | "asc" | "desc"]
        );
      }

      return orderBy(result, ({ data }) => data.createdAt.format, "desc");
    },
    [sortConfig, transactionHistory],
    isEqual
  );

  /**
   * Effect to refetch the transaction history when the refetchTransactionHistory flag is set.
   * This happens once a tx is successfully promoted with the `<TasteTestTransactionToast />` component.
   */
  useEffect(() => {
    if (refetchTransactionHistory) {
      refetch();
      setRefetchTransactionHistory(false);
    }
  }, [refetchTransactionHistory, refetch, setRefetchTransactionHistory]);

  return (
    <FadeInBasic>
      <TableV2
        aria-label="Taste Test Contributions"
        columnWidths="1fr 1fr 1fr 1fr"
        className={toolkitCx.layout.background.default}
        loadingProps={{ isLoading, loadingRows: 5 }}
        numberOfRows={numberOfRows}
        onShowMore={handleShowMoreContributions}
        onSort={handleSort}
        rounded
        sortConfig={sortConfig}
        tableColumns={tableColumns}
        tableData={tableData}
        tableHeaderData={tableHeaders}
        title={
          transactionHistory?.length
            ? t("detail.historyTable.title", { length: transactionHistory.length ?? 0 })
            : t("detail.historyTable.title.empty")
        }
      />
    </FadeInBasic>
  );
};

export default TasteTestTransactionHistoryTable;
